import type { TrackerAdapter, PvOptions } from './types';
import { MAX_DEDUP_CACHE_SIZE } from './constants';

/**
 * 事件去重包装器。在底层 adapter 外套一层去重逻辑，
 * 对相同事件名 + 相同 properties 的事件在可配置时间窗口内只保留第一次上报。
 */
export class DedupAdapter implements TrackerAdapter {
  private adapter: TrackerAdapter;
  private dedupInterval: number;
  private cache: Map<string, number> = new Map();

  constructor(adapter: TrackerAdapter, options: { dedupInterval: number }) {
    this.adapter = adapter;
    this.dedupInterval = options.dedupInterval;
  }

  // ─── 透传方法 ──────────────────────────────────────────

  init(appId: number, options: Record<string, any>): void {
    this.adapter.init(appId, options);
  }

  start(): void {
    this.adapter.start();
  }

  getDefaultProps(): Record<string, any> {
    return this.adapter.getDefaultProps();
  }

  // ─── 去重方法 ──────────────────────────────────────────

  event(eventName: string, properties?: Record<string, any>): void {
    const signature = this._makeSignature(eventName, properties);
    const now = Date.now();

    // 惰性清理过期缓存
    this._cleanup(now);

    const last = this.cache.get(signature);
    if (last !== undefined && now - last < this.dedupInterval) {
      // 相同事件在时间窗口内重复出现，丢弃
      return;
    }

    this.cache.set(signature, now);
    this.adapter.event(eventName, properties);
  }

  // ─── 直接透传（不参与去重） ──────────────────────────────

  pageView(options?: PvOptions): void {
    this.adapter.pageView(options);
  }

  login(userId: string): void {
    this.adapter.login(userId);
  }

  logout(): void {
    this.adapter.logout();
  }

  // ─── 私有方法 ──────────────────────────────────────────

  /** 生成事件签名：eventName + JSON.stringify(sortedProperties) */
  private _makeSignature(eventName: string, properties?: Record<string, any>): string {
    if (!properties || Object.keys(properties).length === 0) {
      return eventName;
    }
    const sorted = Object.keys(properties)
      .sort()
      .reduce((acc, key) => {
        acc[key] = properties[key];
        return acc;
      }, {} as Record<string, any>);
    return `${eventName}::${JSON.stringify(sorted)}`;
  }

  /** 缓存超过上限时清理过期条目 */
  private _cleanup(now: number): void {
    if (this.cache.size > MAX_DEDUP_CACHE_SIZE) {
      for (const [key, ts] of this.cache) {
        if (now - ts > this.dedupInterval) {
          this.cache.delete(key);
        }
      }
    }
  }
}